import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import {
  type AvatarUserType,
  type GroupClassIllustrations,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  ChakraVStack,
  EmptySectionCard,
  FAIconName,
  FireflyButton,
  GroupClassListViewCard,
  GroupListViewCardV2,
  InfiniteScroll,
  ModalV2,
  SkeletonLoader,
  Tab,
  Text,
  TextInputV2,
  useToastMessage,
} from '@equip.health/ui';

import {
  GET_GROUP_CLASS_PAGE_SIZE_V2,
  GroupTypeTagColorMapper,
} from '~/lib/constants/groupClasses.constants';
import { getPreferredFullName } from '~/lib/util/patient.util';
import {
  useLazyGetPatientOrSupportAvailableGroupsListV2Query,
  useLazyGetPatientOrSupportAvailableGroupsListQuery,
  useRegisterOrUnregisterPatientOrSupportFromGroupSectionMutation,
} from '~/store/services/patients';

import type { SelectedGroupState } from '~/lib/types/classes/SelectedGroupState';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';
import SectionsList from './SectionsList';
import PatientRegistrationConfirmationModalV2 from './PatientRegistrationConfirmationModalV2';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import type { AnalyticsEvent } from '~/analytics';

export type RegisterUserForGroupTabProps = {
  name: string;
  userId: string;
  userType: string;
};
interface RegisterUserForGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClick?: ({
    groupClassDetails,
    userIdToRegister,
  }: SelectedGroupState) => void;
  patient: Patient;
  groupId?: string;
  sectionId?: string;
  userId?: string;
}

const TOP_LEVEL_ID = 'register-user-for-group-modal';

const ModalStep = {
  GROUPS: 'groups',
  SECTIONS: 'sections',
  CONFIRMATION: 'confirmation',
} as const;

type ModalStepType = (typeof ModalStep)[keyof typeof ModalStep];

const MODAL_STEP_VIEW_EVENTS = {
  [ModalStep.GROUPS]: GroupSectionEvents.registerUserModalGroupsViewed,
  [ModalStep.SECTIONS]: GroupSectionEvents.registerUserModalSectionsViewed,
  [ModalStep.CONFIRMATION]:
    GroupSectionEvents.registerUserModalConfirmationViewed,
} as const;

const RegisterUserForGroupModal = ({
  isOpen,
  onClose,
  onClick,
  patient,
  groupId,
  sectionId,
  userId,
}: RegisterUserForGroupModalProps) => {
  const [modalStep, setModalStep] = useState<ModalStepType>(ModalStep.GROUPS);

  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();

  const { trackEvent } = useAnalytics();

  const analyticsSource = useMemo(
    () =>
      isGroupsRestructuringEnabled
        ? AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB
        : AnalyticsSourceValues.CALENDAR_GROUPS_TAB,
    [isGroupsRestructuringEnabled],
  );

  const trackModalEvent = (
    eventName: AnalyticsEvent,
    additionalProps?: Record<string, string | undefined>,
  ) => {
    trackEvent(eventName, { Source: analyticsSource, ...additionalProps });
  };

  const [selectedTab, setSelectedTab] = useState<string>();
  const [registerGroupTabs, setRegisterGroupTabs] =
    useState<RegisterUserForGroupTabProps[]>();

  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const [selectedSection, setSelectedSection] = useState<SectionData>();

  const { createToast } = useToastMessage();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [maxPages, setMaxPages] = useState<number>(1);

  const [availableGroups, setAvailableGroups] = useState<GroupClassBaseV2[]>(
    [],
  );

  const [availableGroupsV2, setAvailableGroupsV2] = useState<GroupList[]>([]);

  const [groupClassTitle, setGroupClassTitle] = useState<string>('');

  const [currentUserType, setCurrentUserType] = useState<string>('patient');
  const [
    getGroupClassesListByUserID,
    { data: groupClassesListResponse, isFetching },
  ] = useLazyGetPatientOrSupportAvailableGroupsListQuery();

  const [
    getGroupClassesListByUserIDV2,
    { data: groupClassesListResponseV2, isFetching: isFetchingV2 },
  ] = useLazyGetPatientOrSupportAvailableGroupsListV2Query();

  const [registerPatientSupportGroup, { isLoading: isRegistering }] =
    useRegisterOrUnregisterPatientOrSupportFromGroupSectionMutation();

  const handleSearchInputChange = useMemo(
    () =>
      debounce((value: string) => {
        setCurrentPage(1);
        setAvailableGroups([]);
        setAvailableGroupsV2([]);
        void fetchGroupClassesListByUserID({
          userId: selectedTab,
          userType: currentUserType,
          page: 1,
          searchTerm: value,
        });
      }, 500),
    [selectedTab, currentUserType],
  );

  useEffect(() => {
    return () => {
      handleSearchInputChange.cancel?.();
    };
  }, [handleSearchInputChange]);

  const handleErrorToast = () => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: `${TOP_LEVEL_ID}__warning`,
      text: "We're sorry, we were unable process your request at this time. Please try again.",
      variant: 'critical',
    });
  };

  const updateGroupListState = (
    response:
      | GroupListResponse
      | {
          data: {
            data: GroupClassBaseV2[];
            count: number;
            pageNumber: number;
          };
        },
    isV2: boolean,
    pageNumber: number,
  ) => {
    if (isV2) {
      const v2Response = response as GroupListResponse;
      const maxPageCount = Math.ceil(
        (v2Response.count ?? 0) / GET_GROUP_CLASS_PAGE_SIZE_V2,
      );
      setMaxPages(maxPageCount);

      const isFirstPage = pageNumber === 1;
      setAvailableGroupsV2((prev) =>
        isFirstPage ? v2Response.data : [...prev, ...v2Response.data],
      );
    } else {
      const v1Response = response as {
        data: {
          data: GroupClassBaseV2[];
          count: number;
          pageNumber: number;
        };
      };
      const maxPageCount = Math.ceil(
        v1Response.data.count / GET_GROUP_CLASS_PAGE_SIZE_V2,
      );
      setMaxPages(maxPageCount);

      const isFirstPage = v1Response.data.pageNumber === 1;
      setAvailableGroups((prev) =>
        isFirstPage ? v1Response.data.data : [...prev, ...v1Response.data.data],
      );
    }

    setCurrentPage(pageNumber + 1);
  };

  const fetchGroupClassesListByUserID = async ({
    userId,
    userType,
    page,
    searchTerm,
  }: {
    userId: string;
    userType: string;
    page?: number;
    searchTerm?: string;
  }) => {
    if (!(currentPage <= maxPages || page)) return;

    const pageNumber = page ?? currentPage;
    const apiParams = {
      pageNumber,
      title: searchTerm ?? groupClassTitle,
    };

    const result = await (isGroupsRestructuringEnabled
      ? getGroupClassesListByUserIDV2({
          ...apiParams,
          patientId: patient.userId,
          supportId: userType === 'support' ? userId : undefined,
        })
      : getGroupClassesListByUserID({
          ...apiParams,
          patientId: userId,
          userType,
        }));

    if ('error' in result && result.error) {
      handleErrorToast();
      return;
    }

    if (!('data' in result) || !result.data) return;

    updateGroupListState(result.data, isGroupsRestructuringEnabled, pageNumber);
  };

  useEffect(() => {
    const patientData: RegisterUserForGroupTabProps[] = [
      {
        name: getPreferredFullName(
          patient.chosenName,
          patient.firstName,
          null,
          false,
        ),
        userId: patient.userId,
        userType: 'patient',
      },
    ];

    const supports = patient.supports
      .map((support) => ({
        name: getPreferredFullName(
          support.chosenName,
          support.firstName,
          null,
          false,
        ),
        userId: support.externalId,
        userType: 'support',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    setRegisterGroupTabs([...patientData, ...supports]);

    const selectedUser = userId ?? patient.userId;

    const isPatient = selectedUser === patient.userId;

    setSelectedTab(selectedUser);
    void fetchGroupClassesListByUserID({
      userId: selectedUser,
      userType: isPatient ? 'patient' : 'support',
    });

    if (groupId) {
      setSelectedGroupId(groupId);
      setModalStep(ModalStep.SECTIONS);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const basePayload =
      modalStep !== ModalStep.GROUPS ? { groupId: selectedGroupId } : undefined;

    const confirmationPayload =
      modalStep === ModalStep.CONFIRMATION && selectedSection?.id
        ? { ...basePayload, sectionId: selectedSection.id }
        : basePayload;

    trackModalEvent(
      MODAL_STEP_VIEW_EVENTS[modalStep],
      modalStep === ModalStep.CONFIRMATION ? confirmationPayload : basePayload,
    );
  }, [isOpen, modalStep, analyticsSource, trackEvent]);

  useEffect(() => {
    if (selectedTab && !groupId && !sectionId) {
      setCurrentPage(1);
      setAvailableGroups([]);
      setAvailableGroupsV2([]);
      setSelectedGroupId(null);
      setSelectedSection(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, currentUserType]);

  const groupClassesContainerId = `${TOP_LEVEL_ID}__available-group-classes`;

  const fetchAvailableGroupsToRegisterByPatientExternalId = (
    patientExternalId: string,
    userType: string,
  ) => {
    const groupsContainer = document.getElementById(groupClassesContainerId);

    groupsContainer.scroll({ top: 0 });

    setSelectedTab(patientExternalId);
    setCurrentUserType(userType);
    setCurrentPage(1);
    setGroupClassTitle('');
    void fetchGroupClassesListByUserID({
      userId: patientExternalId,
      userType,
      page: 1,
    });
  };

  const onModalClose = () => {
    setSelectedTab(registerGroupTabs[0]?.userId);
    setCurrentUserType('patient');
    setAvailableGroupsV2([]);
    onClose();
    setSelectedGroupId(null);
    setSelectedSection(null);
  };

  const getHostDetails = (hostsList: GroupClassHost[]) => {
    const hosts = hostsList?.map((host: GroupClassHost) => {
      return {
        ...host,
        name: getDisplayNameOrLegalNameForProvider(
          host.displayFirstName,
          host.firstName,
          host.displayLastName,
          host.lastName,
        ),
        userType: host?.type as AvatarUserType,
        src: host?.avatar?.thumbnailUrl ?? host?.avatar?.url,
        id: host?.externalId,
      };
    });
    return hosts;
  };

  const getUserName = (selectedTab: string) => {
    return registerGroupTabs.find((tab) => tab?.userId === selectedTab).name;
  };

  const renderGroupClassListViewCards = () =>
    availableGroups?.map((groupClass: GroupClassBaseV2) => {
      return (
        <ChakraBox
          key={groupClass.groupClassExternalId}
          marginTop="12px"
          padding="0px 5px 5px"
        >
          <GroupClassListViewCard
            endDate={groupClass.endDate}
            frequency={groupClass.frequency as any} // need to add FrequencyType here
            groupClassExternalId={groupClass.groupClassExternalId}
            hosts={getHostDetails(groupClass.hosts)}
            iconName={groupClass.iconName as GroupClassIllustrations}
            id={`${TOP_LEVEL_ID}__group-class-list-card-${groupClass.groupClassExternalId}`}
            isArchived={groupClass.isArchived}
            isHost={groupClass.isHost}
            isSensitive={groupClass.isSensitive}
            isUserRegistered={groupClass.isUserRegistered}
            nextStartDate={groupClass.nextStartDate}
            onCardClick={() => {
              trackModalEvent(
                GroupSectionEvents.registerUserModalGroupSelected,
              );
              onClick?.({
                groupClassDetails: groupClass,
                userIdToRegister: selectedTab,
                userName: getUserName(selectedTab),
              });
            }}
            startDate={groupClass.startDate}
            title={groupClass.title}
            tooltipWrapper={({ children, providerExternalId }) => (
              <ProviderAvatarTooltipWrapper
                actionTime={groupClass.startDate}
                isRoleAtAction
                placement="left"
                providerExternalId={providerExternalId}
              >
                {children}
              </ProviderAvatarTooltipWrapper>
            )}
          />
        </ChakraBox>
      );
    });

  const renderGroupClassListViewCardsV2 = () =>
    availableGroupsV2?.map(
      ({
        id,
        title,
        iconName,
        ageRanges,
        attendeeRoles,
        groupType,
        groupTypeCode,
        isArchived,
        groupStatusCode,
        isSensitive,
        groupActiveSectionCount,
        treatmentModalities,
        treatmentPrograms,
      }: EnrichedGroupList) => {
        return (
          <ChakraBox key={id} marginTop="12px" padding="0px 5px 5px">
            <GroupListViewCardV2
              id={id}
              title={title}
              iconName={iconName}
              ageRange={ageRanges}
              attendeeRoles={attendeeRoles}
              groupType={groupType}
              groupTypeTagColor={GroupTypeTagColorMapper[groupTypeCode]}
              isArchived={isArchived}
              isDraft={groupStatusCode === 'DRAFT'}
              isSensitive={isSensitive}
              sectionsCount={groupActiveSectionCount}
              isSelected={selectedGroupId === id}
              modalities={treatmentModalities}
              programs={treatmentPrograms}
              onCardClick={() => {
                if (selectedGroupId !== id) {
                  setSelectedSection(null);
                }
                trackEvent(GroupSectionEvents.registerUserModalGroupSelected, {
                  Source: AnalyticsSourceValues.REGISTER_USER_FOR_GROUP_FLOW,
                  'Group title': title,
                  'Group status': groupStatusCode,
                });
                setSelectedGroupId(id);
              }}
            />
          </ChakraBox>
        );
      },
    );

  const activeGroupsLength = (
    (isGroupsRestructuringEnabled ? availableGroupsV2 : availableGroups) ?? []
  ).length;

  const activeIsFetching = isGroupsRestructuringEnabled
    ? isFetchingV2
    : isFetching;

  const PADDING_AND_GAPS = 176;

  const renderGroupsList = () => {
    return (
      <>
        <ChakraFlex justifyContent="space-between">
          <ChakraFlex
            align="center"
            backgroundColor="neutral.background.primary"
            gap="8px"
            marginBottom="20px"
            position="relative"
          >
            {registerGroupTabs?.map((tab) => (
              <Tab
                id={`${TOP_LEVEL_ID}__tabs-${tab.name}`}
                isActive={selectedTab === tab.userId}
                key={`${TOP_LEVEL_ID}__tabs-${tab.name}`}
                onClick={() => {
                  fetchAvailableGroupsToRegisterByPatientExternalId(
                    tab.userId,
                    tab.userType,
                  );
                }}
                title={tab.name}
                variant="bubble"
              />
            ))}
          </ChakraFlex>
          <ChakraBox width="240px">
            <TextInputV2
              id={`${TOP_LEVEL_ID}__group-class-search-input`}
              isReset
              leftIcon={FAIconName.searchRegular}
              onChange={(value) => {
                setGroupClassTitle(value);
                handleSearchInputChange(value);
              }}
              onFocus={() =>
                trackModalEvent(GroupSectionEvents.searchGroupInputClicked)
              }
              placeholder="Search groups"
              value={groupClassTitle}
            />
          </ChakraBox>
        </ChakraFlex>
        <ChakraBox
          height={
            isGroupsRestructuringEnabled
              ? `calc(72vh - ${PADDING_AND_GAPS}px)`
              : 'calc(100vh - 308px)'
          }
          id={groupClassesContainerId}
          overflow="auto"
        >
          <SkeletonLoader
            id={`${TOP_LEVEL_ID}__skeleton-loader`}
            isLoaded={currentPage == 1 ? !activeIsFetching : true}
          >
            {activeGroupsLength > 0 || activeIsFetching ? (
              <InfiniteScroll
                dataLength={activeGroupsLength}
                fetchData={() => {
                  void fetchGroupClassesListByUserID({
                    userId: selectedTab,
                    userType: currentUserType,
                    page: currentPage,
                  });
                }}
                id={`${TOP_LEVEL_ID}__infinite_scroll`}
                scrollableTarget={groupClassesContainerId}
                showLoader={activeIsFetching && activeGroupsLength > 0}
                style={{ overflow: 'hidden' }}
                totalCount={
                  isGroupsRestructuringEnabled
                    ? groupClassesListResponseV2?.count
                    : groupClassesListResponse?.data?.count || 0
                }
              >
                {isGroupsRestructuringEnabled
                  ? renderGroupClassListViewCardsV2()
                  : renderGroupClassListViewCards()}
              </InfiniteScroll>
            ) : (
              <EmptySectionCard
                caption="No results found"
                id={`${TOP_LEVEL_ID}__empty-card`}
              />
            )}
          </SkeletonLoader>
        </ChakraBox>
      </>
    );
  };

  const findSelectedGroup = (groupId: string) => {
    return isGroupsRestructuringEnabled
      ? availableGroupsV2.find((g) => g.id === groupId)
      : availableGroups.find((g) => g.groupClassExternalId === groupId);
  };

  const handleBackButtonClick = (
    event: AnalyticsEvent,
    targetStep: ModalStepType,
  ) => {
    const selectedGroup = findSelectedGroup(selectedGroupId);
    trackModalEvent(event, { 'Group title': selectedGroup?.title });
    setModalStep(targetStep);
  };

  const modalTitle = useMemo(() => {
    switch (modalStep) {
      case ModalStep.GROUPS:
        return 'Register user for group';
      case ModalStep.SECTIONS:
        return (
          <ChakraVStack spacing="0px" alignItems="flex-start" gap="12px">
            <Text variant="h4" id={`${TOP_LEVEL_ID}__section-pick-title`}>
              Choose a section
            </Text>
            <Text variant="body" id={`${TOP_LEVEL_ID}__section-pick-title`}>
              Please pick a section that works with their schedule
            </Text>
          </ChakraVStack>
        );
      case ModalStep.CONFIRMATION:
        return (
          <ChakraVStack spacing="0px" alignItems="flex-start" gap="12px">
            <Text variant="h4" id={`${TOP_LEVEL_ID}__section-pick-title`}>
              Confirm registration
            </Text>
            <Text variant="body" id={`${TOP_LEVEL_ID}__section-pick-title`}>
              Please confirm the group and section details below before
              registering
            </Text>
          </ChakraVStack>
        );
      default:
        return '';
    }
  }, [modalStep]);

  const renderGroupsFooter = () => (
    <ChakraHStack width="100%" justifyContent="space-between" gap="12px">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__sections-back`}
        onClick={() => {
          const selectedGroup = findSelectedGroup(selectedGroupId);
          trackModalEvent(
            GroupSectionEvents.registerUserModalCancelButtonClicked,
            { 'Group title': selectedGroup?.title },
          );
          onModalClose();
        }}
        variant="secondary"
        leftIcon={FAIconName.timesRegular}
      >
        Cancel
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__sections-continue`}
        onClick={() => {
          trackModalEvent(
            GroupSectionEvents.registerUserModalGroupsContinueButtonClicked,
          );
          setModalStep(ModalStep.SECTIONS);
        }}
        rightIcon={FAIconName.chevronRightRegular}
        isDisabled={!selectedGroupId}
      >
        Continue
      </FireflyButton>
    </ChakraHStack>
  );

  const renderSectionsFooter = () => (
    <ChakraHStack width="100%" justifyContent="space-between" gap="12px">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__sections-back`}
        onClick={() =>
          handleBackButtonClick(
            GroupSectionEvents.registerUserModalSectionsBackButtonClicked,
            ModalStep.GROUPS,
          )
        }
        variant="secondary"
        leftIcon={FAIconName.arrowLeftRegular}
        isDisabled={!!groupId}
      >
        Back
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__sections-continue`}
        onClick={() => {
          trackModalEvent(
            GroupSectionEvents.registerUserModalSectionsContinueButtonClicked,
          );
          setModalStep(ModalStep.CONFIRMATION);
        }}
        rightIcon={FAIconName.chevronRightRegular}
        isDisabled={!selectedSection || sectionId === selectedSection?.id}
      >
        Continue
      </FireflyButton>
    </ChakraHStack>
  );

  const handleConfirmRegistration = () => {
    const registerSection = () => {
      registerPatientSupportGroup({
        groupSectionId: selectedSection!.id,
        patientId: patient.userId,
        userExternalId: selectedTab!,
        isRegistered: true,
      })
        .unwrap()
        .then(() => {
          createToast({
            id: 'register-user-success',
            text: 'User registered successfully',
            variant: 'success',
            icon: FAIconName.checkRegular,
          });
          onModalClose();
        })
        .catch(() => {
          createToast({
            id: 'register-user-error',
            text: 'Error registering user',
            variant: 'critical',
            icon: FAIconName.exclamationTriangleRegular,
          });
        });
    };

    if (sectionId && userId) {
      registerPatientSupportGroup({
        groupSectionId: sectionId,
        patientId: patient.userId,
        userExternalId: userId,
        isRegistered: false,
      })
        .unwrap()
        .then(() => {
          registerSection();
        })
        .catch(() => {
          createToast({
            id: 'unregister-user-error',
            text: 'Unable to change section for user. Please try again later.',
            variant: 'critical',
            icon: FAIconName.exclamationTriangleRegular,
          });
        });
    } else {
      registerSection();
    }
  };

  const renderConfirmationFooter = () => (
    <ChakraHStack width="100%" justifyContent="space-between" gap="12px">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__back`}
        onClick={() =>
          handleBackButtonClick(
            GroupSectionEvents.registerUserModalConfirmationBackButtonClicked,
            ModalStep.SECTIONS,
          )
        }
        variant="secondary"
        leftIcon={FAIconName.arrowLeftRegular}
      >
        Back
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__register-user`}
        onClick={() => {
          const selectedGroup = findSelectedGroup(selectedGroupId);
          trackModalEvent(
            GroupSectionEvents.registerUserModalRegisterUserButtonClicked,
            {
              registeredBy: 'provider',
              'Group title': selectedGroup?.title,
              'Section day': selectedSection?.dayOfWeek,
              'Section time': selectedSection?.startDate,
            },
          );
          handleConfirmRegistration();
        }}
        leftIcon={FAIconName.checkRegular}
        isDisabled={!selectedGroupId || !selectedSection}
        isLoading={isRegistering}
      >
        Register user
      </FireflyButton>
    </ChakraHStack>
  );

  const footerByStep: Record<ModalStepType, () => JSX.Element> = {
    [ModalStep.GROUPS]: renderGroupsFooter,
    [ModalStep.SECTIONS]: renderSectionsFooter,
    [ModalStep.CONFIRMATION]: renderConfirmationFooter,
  };

  const renderFooter = useMemo(
    () => footerByStep[modalStep]?.() ?? null,
    [modalStep, selectedGroupId, selectedSection, isRegistering],
  );

  const renderModalContent = () => {
    switch (modalStep) {
      case ModalStep.GROUPS:
        return renderGroupsList();
      case ModalStep.SECTIONS:
        return (
          <SectionsList
            groupId={selectedGroupId}
            excludeSectionId={sectionId}
            onSectionSelect={(section) => {
              const selectedGroup = findSelectedGroup(selectedGroupId);
              trackModalEvent(
                GroupSectionEvents.registerUserModalSectionSelected,
                {
                  'Group title': selectedGroup?.title,
                  'Section day': section.dayOfWeek,
                  'Section time': section.startDate,
                },
              );
              setSelectedSection(section);
            }}
            selectedSection={selectedSection}
          />
        );
      case ModalStep.CONFIRMATION:
        return (
          <PatientRegistrationConfirmationModalV2
            groupId={selectedGroupId}
            selectedSection={selectedSection}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ModalV2
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={onModalClose}
      size="5xl"
      title={modalTitle}
      footerContent={renderFooter}
    >
      <ChakraBox id={`${TOP_LEVEL_ID}__container`}>
        {renderModalContent()}
      </ChakraBox>
    </ModalV2>
  );
};
export default RegisterUserForGroupModal;
